import React, { useState } from 'react';
import axios from 'axios';
import { Formik, useFormikContext } from 'formik';
import * as yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import { FsButton, Heading } from '~/components/core';
import AlertBanner from '~/components/core/AlertBanner';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DatePickerField, { type PickerDate } from '~/components/core/Molecules/Fields/DatePickerField';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import { getDownloadErrorMessage, useFileDownload } from '~/components/hooks/useFileDownload';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { UserModel } from '~/components/types/user-types';
import { reportAxiosError } from '~/Utils';

type AuditMode = 'all' | 'config' | 'contacts';

interface AuditFormValues {
  userId: string;
  startDate: string;
  endDate: string;
  mode: AuditMode;
}

const MODE_OPTIONS: { value: AuditMode; label: string }[] = [
  { value: 'all', label: 'All Changes' },
  { value: 'config', label: 'Configuration Only' },
  { value: 'contacts', label: 'Multi Claim Contacts Only' },
];

const validationSchema = yup.object().shape({
  userId: yup.string().required('User is required'),
  startDate: yup
    .string()
    .required('Start date is required')
    .test('valid-date', 'Invalid date', (value) => value !== 'Invalid date'),
  endDate: yup
    .string()
    .required('End date is required')
    .test('valid-date', 'Invalid date', (value) => value !== 'Invalid date'),
  mode: yup.string().oneOf(['all', 'config', 'contacts']).required(),
});

const InternalAuditLogForm: React.FC = () => {
  const { classes } = useStyles();
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization, users } = useSysconfig();
  const { values, setFieldValue, isValid, isSubmitting, setSubmitting } = useFormikContext<AuditFormValues>();

  const [error, setError] = useState<string | null>(null);
  // Formik's `isSubmitting` already drives this form's button, so the hook's own `isDownloading` is
  // not read here; what the hook is here for is the response saving and the Blob error body.
  const { download } = useFileDownload();

  // Sort users by name for the dropdown
  const sortedUsers = React.useMemo(() => {
    if (!users) return [];
    return [...users].sort((a: UserModel, b: UserModel) => {
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [users]);

  const handleUserChange = (userId: string) => {
    setFieldValue('userId', userId);
    setError(null);
  };

  const handleStartDateChange = (date: PickerDate) => {
    const dateString = date ? date.format('YYYY-MM-DD') : '';
    setFieldValue('startDate', dateString);
    setError(null);
  };

  const handleEndDateChange = (date: PickerDate) => {
    const dateString = date ? date.format('YYYY-MM-DD') : '';
    setFieldValue('endDate', dateString);
    setError(null);
  };

  const handleModeChange = (mode: string) => {
    setFieldValue('mode', mode as AuditMode);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!isValid) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError(null);

    // `download` swallows the request's own failure, but `reportAxiosError` posts to /api/v1/errors
    // and can itself reject on a dead connection -- the finally is what the previous catch-and-report
    // block relied on to re-enable the form in that case.
    try {
      await download(
        () =>
          axios.post(
            `/admin/api/v1/internal_audit/organizations/${organization.id}/user_changes`,
            {
              user_id: values.userId,
              start_date: values.startDate,
              end_date: values.endDate,
              mode: values.mode,
            },
            {
              responseType: 'blob',
            }
          ),
        `audit_report_${values.startDate}_${values.endDate}.csv`,
        {
          // This form shows failures in its own banner, so the hook must not open a dialog on top
          // of it. A failure the server never answered at all (a dropped connection) has nothing to
          // put in the banner beyond the generic copy, so that one is still worth reporting.
          onError: async (err) => {
            if (!axios.isAxiosError(err) || !err.response) {
              await reportAxiosError(err);
            }
            setError(getDownloadErrorMessage(err) ?? 'Failed to generate audit report');
          },
        }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CardDialog noCardTitle>
      <Heading className="my-20">Internal Audit Log</Heading>

      <div className="mb-20">
        <AlertBanner
          alertType={AlertBanner.ALERT_TYPES.INFO}
          withIcon
          note="Generate a CSV report of all configuration changes made by a specific user within a date range. This report includes changes to organization settings, automation rules, user permissions, and other auditable configuration data."
        />
      </div>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SingleSelectField
            id="user"
            label="User *"
            className={classes.textField}
            fullWidth
            value={values.userId}
            onChange={handleUserChange}
            disabled={isSubmitting}
          >
            {sortedUsers.map((user: UserModel) => (
              <MenuItem key={user.id} value={user.id}>
                {user.first_name} {user.last_name} ({user.email})
              </MenuItem>
            ))}
          </SingleSelectField>
        </Grid>

        <Grid item xs={12} md={6}>
          <SingleSelectField
            id="mode"
            label="Audit Mode"
            className={classes.textField}
            fullWidth
            value={values.mode}
            onChange={handleModeChange}
            disabled={isSubmitting}
          >
            {MODE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </SingleSelectField>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <DatePickerField
            id="startDate"
            label="Start Date *"
            className={classes.textField}
            disableFuture
            fullWidth
            value={values.startDate}
            onChange={handleStartDateChange}
            disabled={isSubmitting}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <DatePickerField
            id="endDate"
            label="End Date *"
            className={classes.textField}
            disableFuture
            fullWidth
            value={values.endDate}
            onChange={handleEndDateChange}
            disabled={isSubmitting}
          />
        </Grid>
      </Grid>

      {error && (
        <div className="mt-20">
          <AlertBanner alertType={AlertBanner.ALERT_TYPES.ERROR} withIcon note={error} />
        </div>
      )}

      <div className="mb-20 mt-20">
        <AlertBanner
          alertType={AlertBanner.ALERT_TYPES.WARNING}
          withIcon
          note="Large date ranges (more than 6 months) may take several minutes to process. The report will download automatically when ready."
        />
      </div>

      <div className="mt-20">
        <FsButton color="primary" variant="contained" onClick={handleSubmit} disabled={!isValid || isSubmitting}>
          {isSubmitting ? 'Generating Report...' : 'Download CSV Report'}
        </FsButton>
      </div>
    </CardDialog>
  );
};

const InternalAuditLog: React.FC = () => {
  const initialValues: AuditFormValues = {
    userId: '',
    startDate: '',
    endDate: '',
    mode: 'all',
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} validateOnMount onSubmit={() => {}}>
      <InternalAuditLogForm />
    </Formik>
  );
};

export default InternalAuditLog;
